// The task client's vocabulary (docs/design/mockups/receiving-v1/design-language.html):
// strip, prompt, scan bar, banner, label strip, context rows, action bar, chips, sheet,
// list items. Presentation only; screens decide what goes in them.

import { useState, type ReactNode, type RefObject } from "react";
import { getPrefs } from "../api/client.js";
import { t } from "../i18n.js";
import { Icon, type IconName } from "./icons.jsx";
import { ScannerLine } from "./ScannerLine.jsx";

export function Strip({ left, middle, online, queued, stuck }: { left: ReactNode; middle?: ReactNode; online: boolean; queued: number; stuck: number }) {
  const net = online ? t("strip.online") : t("strip.offline");
  const q = stuck > 0 ? t("strip.queueStuck", { n: stuck }) : t("strip.queue", { n: queued });
  return (
    <>
      <div className="strip">
        <span className="strip-left">{left}</span>
        {middle !== undefined && <span className="strip-mid">{middle}</span>}
        <span className={`net${online ? "" : " off"}${stuck > 0 ? " stuck" : ""}`}>
          {net} · {q}
        </span>
      </div>
      {/* The dedicated scanner's line under every strip, inside the native shell only (owner 2026-10-29). */}
      <ScannerLine />
    </>
  );
}

export function Brand() {
  return (
    <span className="brand-sm">
      <img src="/mark.svg" alt="" width={18} height={18} />
      {t("app.name")}
    </span>
  );
}

export function Prompt({ step, title, sub, children, quiet }: { step?: string | undefined; title: string; sub?: string | undefined; children?: ReactNode; quiet?: boolean | undefined }) {
  return (
    <section className={`prompt${quiet ? " quiet" : ""}`}>
      {step && <div className="step">{step}</div>}
      <h1>{title}</h1>
      {sub && <p className="sub">{sub}</p>}
      {children}
    </section>
  );
}

export function Glance({ n, label }: { n: number; label: string }) {
  return (
    <div className="glance">
      <span className="n">{n}</span>
      <span className="of">{label}</span>
    </div>
  );
}

/** Focus the scan field only when it wants the on-screen keyboard. A focused field with
 *  `inputmode="none"` shows a caret without a keyboard, and on iOS (standalone, pinned body)
 *  that caret is drawn displaced and taps land displaced too (owner 2026-10-28: the caret
 *  blinked in an order row and Meny could not be tapped). The wedge listens on the document,
 *  so a handheld scanner never needs the focus. */
export function focusScan(el: HTMLInputElement | null | undefined): void {
  if (el && el.getAttribute("inputmode") !== "none") el.focus();
}

/** The scan field. A handheld scanner needs neither focus nor the on-screen keyboard
 *  (the wedge listens on the document), so the keyboard stays away unless the device
 *  setting says otherwise or the operator asks for it with the keyboard button. */
export function ScanBar({ inputRef, placeholder, onCamera }: { inputRef: RefObject<HTMLInputElement>; placeholder: string; onCamera?: (() => void) | undefined }) {
  const auto = getPrefs().keyboard;
  const [kb, setKb] = useState(auto);
  const toggleKeyboard = () => {
    const el = inputRef.current;
    if (!el) return;
    const on = !kb;
    setKb(on);
    // iOS reads inputmode at focus time and React re-renders only after the tap, so set the
    // attribute here. Turning the keyboard off leaves the field unfocused.
    el.setAttribute("inputmode", on ? "text" : "none");
    el.blur();
    if (!on) return;
    // The keyboard shows only for a focus inside the tap. iOS skipped the first focus() after
    // the attribute change (owner 2026-11-02: the third tap worked), so a second attempt a
    // moment later, still inside the gesture window, catches it.
    el.focus({ preventScroll: true });
    window.setTimeout(() => {
      if (document.activeElement !== el) { el.blur(); el.focus({ preventScroll: true }); }
    }, 60);
  };
  return (
    <div className="scan ready">
      <Icon name="scan" size={26} />
      <input ref={inputRef} className="scan-input" inputMode={kb ? "text" : "none"} enterKeyHint="go" autoComplete="off" autoCorrect="off" autoCapitalize="characters" spellCheck={false} placeholder={placeholder} aria-label={placeholder} />
      <button type="button" className={`scan-cam${kb ? " on" : ""}`} aria-label={t("scan.keyboard")} aria-pressed={kb} onClick={(e) => { e.stopPropagation(); toggleKeyboard(); }}>
        <Icon name="keyboard" size={26} />
      </button>
      {onCamera && (
        <button type="button" className="scan-cam" aria-label={t("more.camera")} onClick={(e) => { e.stopPropagation(); onCamera(); }}>
          <Icon name="camera" size={26} />
        </button>
      )}
    </div>
  );
}

export type BannerKind = "ok" | "warn" | "bad";
export interface BannerData {
  kind: BannerKind;
  label: string;
  message: string;
}

export function Banner({ kind, label, message }: BannerData) {
  return (
    <div className={`banner ${kind}`} role="status">
      <Icon name={kind} size={28} />
      <div>
        <span className="lbl">{label}</span>
        <span className="msg">{message}</span>
      </div>
    </div>
  );
}

export interface LabelCell {
  title: string;
  value: ReactNode;
  wide?: boolean | undefined;
}

export function LabelStrip({ title, sub, cells, empty }: { title?: string | undefined; sub?: string | undefined; cells?: LabelCell[] | undefined; empty?: string | undefined }) {
  if (!cells || cells.length === 0) return <div className="label"><div className="empty">{empty ?? ""}</div></div>;
  return (
    <div className="label">
      {(title || sub) && (
        <div className="head">
          <b>{title}</b>
          <span>{sub}</span>
        </div>
      )}
      <div className="rows">
        {cells.map((c, i) => (
          <div key={i} className={`cell${c.wide ? " wide" : ""}`}>
            <div className="t">{c.title}</div>
            <div className="v">{c.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export interface RowData {
  k: ReactNode;
  small?: string | undefined;
  q: ReactNode;
  tone?: "ok" | "warn" | undefined;
  /** `head`: a container row with its contents below it; `sub`: one of those contents, indented. */
  level?: "head" | "sub" | undefined;
  /** A tappable row (choosing loose stock to move, for instance). */
  onClick?: (() => void) | undefined;
  selected?: boolean | undefined;
}

/** `compact`: one line per row (name, then small and quantity on the same line), for a list
 *  that is read as a whole rather than row by row — everything scanned on this pallet. */
export function Rows({ rows, more, title, compact }: { rows: RowData[]; more?: string | undefined; title?: string | undefined; compact?: boolean | undefined }) {
  return (
    <div className={`rows3${compact ? " compact" : ""}`}>
      {title && <h2 className="sec">{title}</h2>}
      {rows.map((r, i) => (
        <div key={i} className={`row${r.level ? ` ${r.level}` : ""}${r.onClick ? " tap" : ""}${r.selected ? " on" : ""}`} onClick={r.onClick} role={r.onClick ? "button" : undefined}>
          <span className="k">{r.k}</span>
          <span className="r2">
            <small>{r.small ?? ""}</small>
            <span className={`q${r.tone ? ` ${r.tone}` : ""}`}>{r.q}</span>
          </span>
        </div>
      ))}
      {more && <div className="row more">{more}</div>}
    </div>
  );
}

export function Actions({ children }: { children: ReactNode }) {
  return <div className="actions">{children}</div>;
}

export function Btn({ kind = "secondary", onClick, disabled, children, icon, grow = true }: { kind?: "primary" | "secondary" | "quiet" | "danger"; onClick?: (() => void) | undefined; disabled?: boolean | undefined; children?: ReactNode; icon?: IconName | undefined; grow?: boolean | undefined }) {
  return (
    <button type="button" className={`btn ${kind}${grow ? "" : " nogrow"}`} disabled={disabled} onClick={(e) => { e.stopPropagation(); onClick?.(); }}>
      {icon && <Icon name={icon} size={26} />}
      {children}
    </button>
  );
}

export function Chips<T extends string>({ options, value, onChange }: { options: { value: T; label: string }[]; value: T | null; onChange: (v: T) => void }) {
  return (
    <div className="chips" role="radiogroup">
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} className={`chip${value === o.value ? " on" : ""}`} onClick={(e) => { e.stopPropagation(); onChange(o.value); }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** One value entered at the pallet: large, monospace, takes a scan as well as typing. */
export function Entry({ value, onChange, onEnter, inputMode, placeholder, tag, inputRef, confirm, autoFocus = true }: { confirm?: { label: string; disabled: boolean } | undefined; value: string; onChange: (v: string) => void; onEnter: () => void; inputMode: "text" | "numeric"; placeholder: string; tag?: string | undefined; inputRef?: RefObject<HTMLInputElement> | undefined; /** Off for a second field on the same sheet, so the first keeps the focus (owner 2026-11-03). */ autoFocus?: boolean }) {
  return (
    <div className={`entry${value ? "" : " empty"}`} onClick={(e) => e.stopPropagation()}>
      <input ref={inputRef} className="entry-input" value={value} onChange={(e) => onChange(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onEnter(); } }} inputMode={inputMode} autoComplete="off" autoCorrect="off" autoCapitalize="characters" spellCheck={false} placeholder={placeholder} aria-label={placeholder} autoFocus={autoFocus} />
      {tag && <small>{tag}</small>}
      {confirm && <button type="button" className="entry-ok" aria-label={confirm.label} title={confirm.label} disabled={confirm.disabled} onClick={(e) => { e.stopPropagation(); onEnter(); }}>✓</button>}
    </div>
  );
}

export function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="sheet-back" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
        <div className="sheet-head">
          <b>{title}</b>
          <button type="button" className="btn quiet nogrow" onClick={onClose}>{t("common.close")}</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ListItem({ icon, title, desc, right, onClick, off, tag, tagKind }: { icon?: IconName | undefined; title: string; desc?: ReactNode; right?: ReactNode; onClick?: (() => void) | undefined; off?: boolean | undefined; /** "bad": the tag is a warning in red (owner 2026-11-03: "Låst i affärssystemet borde vara rött"). */ tagKind?: "bad" | undefined; tag?: string | undefined }) {
  return (
    <button type="button" className={`item${off ? " off" : ""}`} onClick={(e) => { e.stopPropagation(); onClick?.(); }} disabled={!onClick}>
      {icon && <Icon name={icon} size={36} className="ic" />}
      <span className="tx">
        <span className="ti">{title}</span>
        {desc && <span className="de">{desc}</span>}
      </span>
      {tag ? <span className={`cfg${tagKind === "bad" ? " bad" : ""}`}>{tag}</span> : right !== undefined ? right : onClick ? <Icon name="chev" size={24} className="chev" /> : null}
    </button>
  );
}
